import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMapPin } from 'react-icons/fi';
import { generateSlug } from '../utils/slugify';
import { getImageUrl } from '../utils/imageUtils';

const MobileProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const isWealth = project.isWealthProject;
  
  let imgUrl = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80';
  if (isWealth && project.photo) {
    imgUrl = project.newImageUrl || (project.photo.startsWith('http') ? project.photo : getImageUrl(project.photo, true));
  } else if (!isWealth && project.projectImage) {
    imgUrl = getImageUrl(project.projectImage);
  }

  const title = isWealth ? (project.projectName || project.officeAddress || project.companyName) : project.title;
  const builderName = isWealth ? project.companyName : project.builderName;
  const locationText = isWealth ? (project.projectAddress || project.city) : `${project.address?.line1 ? `${project.address.line1}, ` : ''}${project.address?.city || ''}`;
  const projectLink = isWealth 
    ? `/wealth-project/${generateSlug(title || 'project')}/${project._id}`
    : `/project/${generateSlug(title || 'project')}/${project._id}`;

  return (
    <div 
      className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden w-full cursor-pointer flex flex-col active:scale-[0.98] transition-transform duration-200"
      onClick={() => navigate(projectLink)}
    >
      <div className="relative w-full aspect-[16/9] bg-gray-100">
        <img 
          src={imgError ? 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80' : imgUrl} 
          onError={() => setImgError(true)} 
          alt={project.title} 
          className="absolute inset-0 w-full h-full object-cover" 
        />
        
        {/* Badges */}
        <div className="absolute top-2 left-2 z-10 flex flex-col items-start gap-1">
          {project.projectType && (
            <span className="bg-white/90 backdrop-blur text-primary text-[10px] font-bold px-2 py-1 rounded shadow-sm border border-primary/20">
              {project.projectType}
            </span>
          )}
        </div>
        <div className="absolute bottom-2 right-2 z-10">
          {project.projectStatus && (
            <span className="bg-black/60 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">
              {project.projectStatus}
            </span>
          )}
        </div>
      </div>
      
      <div className="p-3 flex flex-col gap-1">
        <h3 className="font-bold text-[15px] text-gray-900 line-clamp-1 leading-tight">
          {title || 'Untitled Project'}
        </h3>
        
        <p className="text-[12px] text-gray-500 flex items-center gap-1 line-clamp-1">
          <FiMapPin size={12} className="flex-shrink-0 text-gray-400" /> 
          {locationText}
        </p>
        
        <div className="mt-1 flex flex-wrap gap-1">
          {project.projectType === 'Plots / Land' ? (
            <>
              {project.extent && (
                <span className="text-[10px] font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-100">
                  Extent: {project.extent}
                </span>
              )}
              {project.totalPlots && (
                <span className="text-[10px] font-medium bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
                  Plots: {project.totalPlots}
                </span>
              )}
            </>
          ) : project.bedrooms && project.bedrooms !== 'N/A' && (
            project.bedrooms.split(',').slice(0, 3).map((config, idx) => (
              <span key={idx} className="text-[10px] font-medium bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                {config.trim()}
              </span>
            ))
          )}
        </div>
        
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold">
            {builderName ? builderName.substring(0, 2).toUpperCase() : 'RP'}
          </div>
          <span className="text-[12px] font-medium text-gray-700 line-clamp-1">
            {builderName || 'Real Properties'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MobileProjectCard;
